<template>
  <div class="menu-pc-tree-container">
    <el-tree
      ref="tree"
      :data="data"
      :props="defaultProps"
      :expand-on-click-node="false"
      :allow-drop="allowDrop"
      node-key="id"
      @node-drop="handleDrop"
      @node-click="nodeClick"
      :filter-node-method="filterNode"
      draggable>
      <span class="custom-tree-node" slot-scope="{ node, data }">
        <span :title="node.label">{{ node.label }}</span>
        <span class="show-icon" :ref="data.id">
          <template v-if="showAddIcon(data, node)">
            <el-tooltip popper-class="menu-tree-tooltip" effect="dark" content="添加子类" placement="bottom">
            <i class="el-icon-plus" @click.stop.prevent="appendNode(node, data)"></i>
          </el-tooltip>
          </template>
          <template v-if="showDeleteIcon(data)">
            <el-tooltip popper-class="menu-tree-tooltip" effect="dark" content="删除" placement="bottom">
              <i class="el-icon-delete" @click.stop.prevent="deleteNode(node, data)"></i>
            </el-tooltip>
          </template>
        </span>
      </span>
    </el-tree>
  </div>
</template>

<script>
import { UUID } from '@/plugins/common'
export default {
  name: 'menu-pc-tree',
  props: {
    data: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      defaultProps: {
        children: 'children',
        label: 'name'
      },
      currentId: ''
    }
  },
  mounted () {
    // 默认选中第一个tree
    if (this.data && this.data.length) {
      this.setCurrentKey(this.data[0].id)
      this.$emit('nodeClick', this.data[0], '')
      let node = this.getNode(this.data[0])
      // 默认展开第一个
      node && node.expand()
    }
  },
  methods: {
    // 拖拽成功完成时触发的事件
    handleDrop (draggingNode, dropNode, dropType, ev) {
      // 如果被拖拽的节点是拖拽到某个节点的下面，则展开该节点
      let children = dropNode.data.children
      if (children.find(c => c.id === draggingNode.data.id)) {
        !dropNode.expanded && dropNode.expand()
      }
      // 拖拽完成的时候选中被拖拽的节点
      this.$emit('handleDrop', draggingNode, dropNode, dropType)
      this.$nextTick(() => {
        this.setCurrentKey(draggingNode.data.id)
        this.nodeClick(draggingNode.data, draggingNode)
      })
    },
    allowDrop (draggingNode, dropNode, type) {
      // 系统内置的菜单不可修改层级
      if (draggingNode && draggingNode.data && parseInt(draggingNode.data.isBuiltIn) === 1) {
        return draggingNode.level === dropNode.level && type !== 'inner'
      }
      return true
    },
    // 节点点击
    nodeClick (data, node) {
      let parentName = ''
      if (typeof node === 'object') {
        if (node.level !== 1) {
          if (node && node.parent && node.parent.data) {
            parentName = node.parent.data.name
          }
        } else {
          parentName = ''
        }
      } else {
        parentName = node
      }
      this.$emit('nodeClick', data, parentName)
    },
    // 新增节点
    appendNode (node, data) {
      // 如果当前节点下有未保存的节点，判断是否选中，未选中，直接选中即可
      if (data && data.children && data.children.length) {
        let noSaveData = data.children.find(c => c.isNew)
        if (noSaveData) {
          // 本来是判断选中的节点是否为当前添加的节点，但是父组件中使用了 isCurrent 会导致选中效果失效，所以重新选中
          this.setCurrentKey(noSaveData.id)
          this.nodeClick(noSaveData, node.data.name)
          return false
        }
      }
      // 获取当前选中节点，判断是否为未保存节点，如果是未保存节点，则删除该节点
      let currentNode = this.getCurrentNode()
      if (currentNode.isNew) {
        // 获取node节点
        let data = this.getNode(currentNode)
        data && this.$refs.tree.remove(data)
      }
      let newData = this.getNewTree(data)
      if (data) {
        if (data.children) {
          data.children.push(newData)
        } else {
          data.children = [newData]
        }
      }
      node.expand()
      this.$nextTick(() => {
        this.setCurrentKey(newData.id)
        this.nodeClick(newData, node.data.name)
      })
    },
    // 删除节点
    deleteNode (node, data) {
      let hint = (data.children && data.children.length > 0) ? '菜单下的子菜单也会被删除，您确定删除吗？' : '您确定要删除吗？'
      this.$confirm(hint, ' ', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        customClass: 'deleteMenuMessageBox',
        distinguishCancelAndClose: true,
        center: true
      }).then(res => {
        let parent = node.parent
        let currentNode = this.getCurrentNode()
        this.$refs.tree.remove(node)
        // 如果当前选中的节点是删除的节点，删除完之后默认选中父节点
        if (currentNode.id === data.id) {
          let currentId = parent.data.id
          // 删除的是一级菜单，选中第一个
          if (!currentId || node.level === 1) {
            if (this.data && this.data.length) {
              currentId = this.data[0].id
            }
          }
          this.setCurrentKey(currentId)
          if (node.level === 1) {
            this.nodeClick(this.data[0], this.data[0].name)
          } else {
            this.nodeClick(parent.data, parent)
          }
        }
        // 保存过后的菜单才需要走接口删除
        !data.isNew && this.$axios.delete(`/auth-api/menus/${data.id}`).then(() => this.$message.success('删除成功!'))
        // 如果删除过后的菜单没有了，需要清空右侧表单
        if (!this.data || this.children.length === 0) {
          return this.$emit('deleteActiveForm')
        }
      }).catch(() => {})
    },
    // 新增的时候获取数据模型
    getNewTree (data) {
      return {
        id: UUID.create(),
        name: '未命名',
        parentId: data ? data.id : '',
        link: '',
        functionType: '',
        icon: '',
        isBuiltIn: 0,
        isNew: true,
        appModel: {
          workbenchType: false,
          appIcon: ''
        },
        children: []
      }
    },
    // 根据id选中节点
    setCurrentKey (id) {
      return this.$refs.tree && this.$refs.tree.setCurrentKey(id)
    },
    // 获取当前选中的节点
    getCurrentNode () {
      return this.$refs.tree ? this.$refs.tree.getCurrentNode() : undefined
    },
    // 节点过滤
    filterNode (value, data) {
      if (!value) return true
      return data.name.indexOf(value) !== -1
    },
    getNode (param) {
      return this.$refs.tree.getNode(param)
    }
  },
  computed: {
    // 默认的几个菜单没有删除
    showDeleteIcon () {
      return data => parseInt(data.isBuiltIn) !== 1
    },
    // 新建的节点没有保存的时候是没有新建按钮的
    showAddIcon () {
      // 系统内置的报表菜单是可以添加子菜单的
      return (data, node) => parseInt(data.isBuiltIn) === 1 ? true : !data.isNew
    }
  }
}
</script>

<style lang="scss">
.menu-pc-tree-container {
  height: 100%;
  min-width: 100%;
  .el-tree {
    min-width: 100%;
    .el-tree-node {
      min-width: 100%;
      .el-tree-node__content {
        height: 30px;
        line-height: 30px;
        min-width: 50%;
        box-sizing: content-box;
        .el-tree-node__expand-icon {
          font-size: 14px;
        }
        .el-tree-node__label {
          font-size: 12px;
        }
        .custom-tree-node {
          width: calc(100% - 20px);
          height: 24px;
          line-height: 24px;
          display: flex;
          align-items: center;
          & > span:first-child {
            display: inline-block;
            max-width: calc(100% - 65px);
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            margin-right: 10px;
            min-width: 25px;
          }
          .show-icon {
            color: #C2C3C4;
            display: none;
            i + i {
              margin-left: 10px;
              &:hover {
                color: #fb9391;
              }
              &:active {
                color: red;
              }
            }
            i:first-child {
              &:hover {
                color: var(--light-2, #5cb6ff);
              }
              &:active {
                color: var(--light-1, #54abf2);
              }
            }
          }
          &:hover {
            .show-icon {
              display: inline-block;
            }
          }
        }
      }
    }
    .is-current {
      background-color: #fff;
      & > .el-tree-node__content {
        & > .custom-tree-node {
          & > span:first-child {
            color: var(--light-1, #54ABF1);
          }
          .show-icon {
            display: inline-block;
          }
        }
      }
    }
    & > .is-expanded {
      & > .el-tree-node__content {
        & > .expanded {
          color: var(--light-1, #54ABF1);
        }
      }
    }
    .is-drop-inner {
      background-color: #ECF5FC;
      border-top: 1px dashed var(--light-1, #54ABF1);
    }
    & > .el-tree-node {
      & > .el-tree-node__content {
        border-bottom: 1px solid #EDF2F5;
        height: 38px;
        line-height: 38px;
      }
    }
    .el-tree-node>.el-tree-node__children {
      overflow: unset;
      min-width: 100%;
    }
  }
}
.menu-tree-tooltip {
  padding: 5px 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/system/system/menu/menu-pc-tree.vue